import React, { createContext, useContext, useEffect, useState } from "react";

import styled from "styled-components/macro";

import classNames from "classnames";
import { motion, useIsPresent } from "framer-motion";

import MeasureComponent from "Utilities/Measurements";

import { ResizingContainerContext } from "./SharedResizingContainer";

const ResizingChildContext = createContext();

export function SharedResizingChild({ children, cancelExit, ...props }) {
  const isPresent = useIsPresent();

  const ResizingContext = useContext(ResizingContainerContext);

  const [parentIsMountedSaved] = useState(ResizingContext?.parentIsMounted);
  const [measurements, setMeasurements] = useState();
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

  useEffect(() => {
    if (measurements && !ResizingContext?.isResizing) setHasAnimatedIn(true);
  }, [measurements, ResizingContext?.isResizing]);

  if (!ResizingContext) return children;

  const {
    setNewMeasurements,
    direction,
    bounce,
    isResizing,
    forceWidthWrapper,
  } = ResizingContext;

  const updateMeasurements = (measurements) => {
    setMeasurements(measurements);
    setNewMeasurements({
      width: measurements.width,
      height: measurements.height,
    });
  };

  return (
    <MeasureComponent
      forceWidthWrapper={forceWidthWrapper}
      measurements={measurements}
      setMeasurements={updateMeasurements}
    >
      <Wrapper
        className={classNames({
          isResizing:
            parentIsMountedSaved && isPresent && (!hasAnimatedIn || isResizing),
        })}
        initial={
          parentIsMountedSaved
            ? { opacity: 0, x: direction === "left" ? "80%" : "-80%" }
            : null
        }
        animate={{
          opacity: 1,
          x: 0,
          transition: bounce
            ? {}
            : { type: "tween", duration: 0.3, ease: "easeOut" },
        }}
        exit={
          cancelExit
            ? null
            : {
                opacity: 0,
                x: direction === "left" ? "-80%" : "80%",
                transition: { type: "tween", duration: 0.3, ease: "easeOut" },
                position: "absolute",
              }
        }
        {...props}
      >
        <ResizingChildContext.Provider value={{ isResizing, hasAnimatedIn }}>
          {children}
        </ResizingChildContext.Provider>
      </Wrapper>
    </MeasureComponent>
  );
}

const Wrapper = styled(motion.div)`
  width: 100%;
  &.isResizing {
    position: absolute;
  }
`;

export function useResizingChildContext() {
  const context = useContext(ResizingChildContext);
  if (!context) {
    throw new Error(
      "useResizingChildContext must be used inside a SharedResizingChild"
    );
  }

  return context;
}
